import React, { useState, useEffect } from 'react';

// React DateTime
import Datetime from 'react-datetime';
import moment from 'moment';

// Components
import Breadcrumbs from '../../../components/Breadcrumbs';
import SearchAndFilter from '../../../components/Search/SearchAndFilter';
import TeasuryTable from '../../../components/Content/TeasuryTable';
import Pagination from '../../../components/Footer/Pagination';

import { formatDate, formatDateOnChange } from "../../../../utils/DateUtils";
import { parseCurrency } from '../../../../utils/SamuiUtils';

// Keyboard Navigation
import { useDataTableKeyboardNavigation, getDataTableRowStyle, getDataTableKeyboardInstructions } from '../../../../utils/DataTableKeyboardNavigation';

const Main = ({ name, dataMasterList, dataDetailList, onPageInsert, onPageUpdate }) => {
  const [filteredDetailList, setFilteredDetailList] = useState([]);
  const [whDocNo, setWhDocNo] = useState("");
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(
    localStorage.getItem('itemsPerPageDropdown') || 10
  );
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState(
    localStorage.getItem('searchTerm') || ''
  );
  const [filteredItems, setFilteredItems] = useState([]);
  
  // Date filter states
  const [startDate, setStartDate] = useState(() => {
    const storedStartDate = localStorage.getItem('startDate');
    return storedStartDate || moment().startOf('month').format('YYYY-MM-DD');
  });
  
  const [endDate, setEndDate] = useState(() => {
    const storedEndDate = localStorage.getItem('endDate');
    return storedEndDate || moment().endOf('month').format('YYYY-MM-DD');
  });

  // Keyboard navigation
  const [selectedRowIndex, setSelectedRowIndex] = useState(0);

  // Field mappings for keyboard navigation
  const fieldMappings = {
    WHDoc_Id: 'WHDoc_Id'
  };

  // ทดสอบการทำค้นหาโดย SpriteZadis (ยังไม่สมบูรณ์ เดี๋ยวมาทำต่อ) - ไม่ได้ใช้แล้ว
  const [searchItemTerm, setSearchItemTerm] = useState('');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const handleChangeDate = (value, name) => {
    // ตรวจสอบว่า value เป็น moment object หรือไม่
    const newValue = value && value instanceof moment ? value.format('YYYY-MM-DD') : value;

    if (name === 'fromDate') {
      setFromDate(formatDateOnChange(newValue));
    } else {
      setToDate(formatDateOnChange(newValue));
    }
  };

  // Initialize data
  useEffect(() => {
    if (dataMasterList && dataMasterList.length > 0) {
      setCurrentPage(1);
      setSelectedRowIndex(0); // Reset selected row when data changes
      filterItems();
    } else {
      setFilteredItems([]);
    }
  }, [dataMasterList]);

  // Filter when search term or dates change
  useEffect(() => {
    setCurrentPage(1);
    setSelectedRowIndex(0); // Reset selected row when filters change
    filterItems();
  }, [searchTerm, startDate, endDate, dataMasterList]);

  // Filter items function
  const filterItems = () => {
    try {
      let filtered = [...dataMasterList];

      // Date filtering
      const start = moment(startDate, 'YYYY-MM-DD');
      const end = moment(endDate, 'YYYY-MM-DD');

      filtered = filtered.filter((item) => {
        const itemDate = moment(item.WHDoc_Date || item.SO_Date, 'YYYY-MM-DD');
        return itemDate.isBetween(start, end, null, '[]');
      });

      // Search filtering
      if (searchTerm.trim() !== '') {
        const lowercasedSearchTerm = searchTerm.toLowerCase();
        filtered = filtered.filter((item) =>
          (item.WHDoc_No && item.WHDoc_No.toLowerCase().includes(lowercasedSearchTerm)) ||
          (item.WHDoc_Cust_Name && item.WHDoc_Cust_Name.toLowerCase().includes(lowercasedSearchTerm)) ||
          (item.SO_No && item.SO_No.toLowerCase().includes(lowercasedSearchTerm)) ||
          (item.AR_Name && item.AR_Name.toLowerCase().includes(lowercasedSearchTerm)) ||
          (item.SOStatus_Name && item.SOStatus_Name.toLowerCase().includes(lowercasedSearchTerm))
        );
      }

      setFilteredItems(filtered);
    } catch (error) {
      console.error('Filter error:', error.message);
      setFilteredItems([]);
    }
  };
  
  // Pagination calculations
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    setSelectedRowIndex(0); // Reset selected row when page changes
    localStorage.setItem('currentPage', pageNumber);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredItems?.slice(indexOfFirstItem, indexOfLastItem) || [];

  // Keyboard navigation - only enable when currentItems is available
  useDataTableKeyboardNavigation(
    true, // isActive
    currentItems.length > 0 ? currentItems : [],
    selectedRowIndex,
    setSelectedRowIndex,
    (selectedItem) => {
      setSelectedRowIndex(currentItems.findIndex(item => item.WHDoc_Id === selectedItem.WHDoc_Id));
      onRowSelected(selectedItem);
    },
    fieldMappings,
    currentPage,
    handlePageChange,
    filteredItems?.length || 0,
    itemsPerPage,
    null, // getReport function
    {
      customKeyHandlers: {
        'Enter': (event, { selectedRowIndex, currentItems }) => {
          event.preventDefault();
          if (currentItems && currentItems[selectedRowIndex]) {
            onPageUpdate(currentItems[selectedRowIndex].WHDoc_No);
          }
        },
        'F5': (event) => {
          event.preventDefault();
          const searchInput = document.querySelector('input[type="text"]');
          if (searchInput) {
            searchInput.focus();
          }
        },
        'Escape': (event) => {
          event.preventDefault();
          setSearchTerm('');
        }
      }
    }
  );

  const onRowSelected = (fromData) => {
    // พี่คินอยากให้กระพริบ เดี๋ยวไปร์ทจัดให้
    setFilteredDetailList([]);
    setWhDocNo("");

    setTimeout(() => {
      const filteredDetail = dataDetailList.filter(data => parseCurrency(data.WHDoc_Id) === fromData.WHDoc_Id);

      if (filteredDetail && filteredDetail.length > 0) {
        setFilteredDetailList(filteredDetail);
      } else {
        setFilteredDetailList([]);
      }

      setWhDocNo(fromData.WHDoc_No);
    }, 50);
  };

  // ฟังก์ชันเดิมไม่ใช้แล้ว แต่เก็บไว้เพื่อไม่ให้โค้ดพัง
  const filterDataByDate = (data) => {
    return true;
  };

  // ใช้ filteredItems แทนที่จะกรองโดยตรงจาก dataMasterList
  const filteredItemData = filteredItems || [];

  return (
    <>
      <Breadcrumbs page={name} items={[
        { name: 'ขายสินค้า', url: '/sellproducts' },
        { name: name, url: '/order-product' },
      ]} />
      <div className="container-fluid">
        {/* Search and Filter Section */}
        <SearchAndFilter
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          searchPlaceholder="ค้นหาเลขเอกสาร, ลูกค้า..."
          startDate={startDate}
          endDate={endDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          showDateFilter={true}
          onAddNew={onPageInsert}
          addButtonText={`สร้าง${name}`}
          showAddButton={true}
        />
        
        <div className="card py-3 hidden-scrollbar" style={{ maxHeight: "80vh", overflowY: "auto" }}>
          <div className="row">
            <div className="col-5 mb-1" style={{ borderRight: '2px solid #c7c8c9' }}>
              <div className="table-responsive" style={{ maxHeight: '650px', overflowY: 'auto' }}>
                <table id="basic-datatables" className="table table-striped table-hover">
                  <thead className="thead-dark">
                    <tr>
                      <th className="text-center" style={{ width: '25%' }}>วันที่</th>
                      <th className="text-center" style={{ width: '35%' }}>เลขเอกสาร</th>
                      <th className="text-center" style={{ width: '30%' }}>ลูกค้า</th>
                      <th className="text-center" style={{ width: '10%' }}>
                        <i className="fa fa-edit" />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItemData.length > 0 && currentItems.length > 0 ? (
                      currentItems.map((data, index) => (
                        <tr
                          key={data.WHDoc_Id || index + 1}
                          onClick={() => {
                            setSelectedRowIndex(index);
                            onRowSelected(data);
                          }}
                          style={{ 
                            cursor: 'pointer',
                            ...getDataTableRowStyle(index, selectedRowIndex)
                          }}>
                          <td className="text-center">{formatDate(data.WHDoc_Date)}</td>
                          <td className="text-center">{data.WHDoc_No}</td>
                          <td className="text-center">{data.WHDoc_Cust_Name}</td>
                          <td className="text-center">
                            <button className="btn btn-sm" onClick={(e) => {
                              e.stopPropagation();
                              onPageUpdate(data.WHDoc_No);
                            }}>
                              <i className="fa fa-edit" />
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4">
                          <center>
                            <h5>ไม่พบข้อมูล</h5>
                          </center>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination & Items per page selector */}
              <div className="bg-white mt-2" style={{ padding: '8px 0' }}>
                <div className="row m-0">
                  <div className="col-12 d-flex justify-content-between align-items-center flex-wrap py-2">
                    <div className="d-flex align-items-center flex-wrap">
                      <span className="me-2">แสดง:</span>
                      <select
                        className="form-select me-2"
                        style={{ width: 'auto', minWidth: '70px' }}
                        value={itemsPerPage}
                        onChange={(e) => {
                          const newItemsPerPage = parseInt(e.target.value);
                          setItemsPerPage(newItemsPerPage);
                          localStorage.setItem('itemsPerPageDropdown', newItemsPerPage);
                          setCurrentPage(1);
                          setSelectedRowIndex(0);
                        }}
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                      <span>รายการต่อหน้า | แสดง {indexOfFirstItem + 1} ถึง {Math.min(indexOfLastItem, filteredItems?.length || 0)} จาก {filteredItems?.length || 0} รายการ</span>
                    </div>
                    <div className="d-flex align-items-center flex-wrap">
                      <Pagination
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        itemsPerPage={itemsPerPage}
                        dataMasterList={filteredItems || []}
                        handlePageChange={handlePageChange}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-7 mb-3">
              <div className="d-flex align-items-center bg-light p-3 rounded shadow-sm position-relative">
                <p className="card-title mb-0">
                  เอกสารเลขที่: {whDocNo}
                </p>
              </div>
              <TeasuryTable dataList={filteredDetailList} />
            </div>
          </div>
        </div>
        
        {/* Keyboard Instructions */}
        <div className="container-fluid mt-2">
          <div className="row">
            <div className="col-12">
              <small className="text-muted">
                {getDataTableKeyboardInstructions()}
              </small>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Main;